// @flow
import * as React from 'react';
import styled, { css } from 'styled-components';
import _ from 'lodash';

import { getActivePlayerIdsForRound } from '../../../helpers/team/getActivePlayerIdsForRound';
import type {
	TPLeague,
} from "../../../modules/types/league";
import type {
	TRound,
	TPosition
} from "../../../modules/types";

import { RotatedLabel } from '../../../components';
import PlayerInfo from "../../../components/PlayerInfo";

import colors from '../../../assets/css/colors';
import { getYetToPlay, playersMatchIsScheduled } from '../../../helpers/team';
import { below } from '../../../assets/css/media';
import { getPlayerScoreByRound } from '../../../helpers/players';
// import { lastRoundFinished } from '../../../helpers/rounds';

const Container = styled.div`
	margin: 0 0 30px;
	button[rel="compressed-view"], button[rel="uncompressed-view"] {
		${below.desktop`
			display: none;
		`};
	}
`;

const PositionsPlayerList = styled.div``;

const StyledRotatedLabel = styled(RotatedLabel)`
	flex-basis: 20px;
`;

const PlayerComparisonRow = styled.div`
	display: flex;
	justify-content: space-between;
	border-bottom: 1px solid ${colors.lightGreen};
`;

const PositionHeader = styled.div`
	display: block;
	background: ${colors.lightGrey};
	color: ${colors.darkGrey};
	font-size: 12px;
	text-transform: uppercase;
	text-align: center;
	width: 100%;
	font-weight: 700;
	line-height: 1.5;
	border-bottom: 1px solid ${colors.lightGreen};
	padding-top: 2px;
`;

const PlayerCount = styled.div`
	font-size: 12px;
	font-weight: 200;
	color: pink;
`;

const ScoreWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	flex: 0 0 70px;
	${below.tablet`
		flex: none;
	`};
`;

const ProjectedScoreContainer = styled.div`
	display: inline-block;
	border: 1px solid ${colors.coachPurple};
	border-radius: 10px;
	padding: 3px 10px;
`;

const Score = styled.div`
	font-size: 12px;
	font-weight: 700;

	${below.tablet`
		font-size: 10px;
	`};

	${({ has_assistant_coach, match_status }) =>
		has_assistant_coach && match_status === "scheduled" && `
		color: ${colors.coachPurple};
	`}
	${({ value, match_status }) => value === 'DNP' && match_status === 'complete' && `
		height: 2em;
		width: 3em;
		border-radius: 1.5em;
		background: ${colors.form_border};
		color: white;
		display: flex;
		align-items: center;
		justify-content: center;
	`};

	${({ match_status }) => match_status === 'playing' && `
		height: 2em;
		width: 3em;
		border-radius: 1.5em;
		background: ${colors.liveGreen};
		color: white;
		display: flex;
		align-items: center;
		justify-content: center;
	`}

`;

const PlayerInfoStyled = styled(PlayerInfo)`
	flex: 1 0 auto;
	${below.desktop`
		margin: 0;
	`};

`;

const Player = styled.div`
	box-sizing: border-box;
	display: flex;
	width: calc(100% - 20px);
	justify-content: space-between;
	flex-direction: ${({ align }) => align === 'right' ? 'row-reverse' : 'row'};
	align-items: center;
	${({ align }) => align === 'right' ? 'padding-left: 3%' : 'padding-right: 3%'};
	${({ align }) => align === 'right' && `
		border-left: 1px solid ${colors.lightGreen};
	`};
`;


const PlayerRowPart = styled.div`
	display: flex;
	flex-direction: ${({ reverse }) => reverse? 'row-reverse': 'row' }
	justify-content: ${({ reverse }) => reverse? 'flex-end': 'flex-start' };
	align-items: center;
	width: 50%;
	background-color: white
	${({ players_score_does_not_count }) => players_score_does_not_count && css`
		background-color: ${colors.lightGrey};
	`};
`;

type Props = {
	home_team: Object,
	away_team: Object,
	players: Object,
	positions: Array<TPosition>,
	round: TRound,
	actual_round: Object,
	has_assistant_coach: boolean,
	league: TPLeague,
	rounds: Array<TRound>,
	openPlayerPopup: Function
}

type State = {
	is_compressed: boolean
}

const getSquadMatch = (matches, squad_id) => {
	// get the match
	return Array.isArray(matches)? matches.find(match =>
		match.away_squad_id === squad_id || match.home_squad_id === squad_id): {};
};

class TeamsComparisonTable extends React.Component<Props, State> {
	constructor() {
		super();
		_.bindAll(this, ['shouldPlayersScoreBeDoubled', 'playersScoreDoesNotCount']);
	}

	get positions_list() {
		const {
			positions
		} = this.props;

		return positions ? positions.concat([{ id: 'bench', full_name: 'Interchange' }]): null;
	}

	is_team_player_captain(team: Object, player_id: number) {
		return _.get(team, 'lineup.captain') === player_id;
	}

	is_team_player_vice_captain(team: Object, player_id: number) {
		return _.get(team, 'lineup.vice_captain') === player_id;
	}

	is_team_player_emergency(team: Object, player_id: number) {
		return (
			_.isObject(_.get(team, 'lineup.emergency')) &&
			Object.values(team.lineup.emergency).includes(player_id)
		);
	}

	getPlayerSpecialPosition(team: Object, player_id: number) {
		if (this.is_team_player_captain(team, player_id)) {
			return 'captain';
		}
		else if (this.is_team_player_vice_captain(team, player_id)) {
			return 'vice_captain';
		}
		else if (this.is_team_player_emergency(team, player_id)) {
			return 'emergency';
		}

		return null;
	}

	shouldPlayersScoreBeDoubled(player: Object, team: Object, round: Object) {
		const { players } = this.props;
		const { lineup } = team;

		// check if player is captain
		if(player.id === lineup.captain) {
			return true;
		}


		// check if player is vice captain and if captain is DNP
		if(
			lineup.vice_captain &&
			!playersMatchIsScheduled(lineup.captain, players, round) &&
			lineup.vice_captain === player.id
		) {
			if(_.get(players, `${lineup.captain}.stats.scores.${round.id}`, null) === null) {
				return true;
			}
		}
		return false;
	}

	playersScoreDoesNotCount(
		player: Object,
		team: Object,
		is_emergency_enabled: boolean,
		is_emergency: boolean,
		position: Object
	) {
		const { round, players } = this.props;

		if(round.status === "scheduled") {
			return false;
		}

		const active_player_ids = getActivePlayerIdsForRound(team.lineup, players, round);

		return !active_player_ids.includes(player.id);
	}

	renderPlayerCount(player_pos_ids: number[], position: TPosition) {
		const { players, round } = this.props;
		const yet_to_play = getYetToPlay(player_pos_ids, players, round);

		return (
			<PlayerCount>
				{yet_to_play} yet to play
			</PlayerCount>
		);
	}

	renderPositionHeader(
		position: TPosition, home_pos_player_ids: number[], away_pos_player_ids: number[]
	) {
		if(position.id !== 'bench') {
			return null;
		}
		return (
			<PositionHeader>
				Bench
			</PositionHeader>
		);
	}

	// eslint-disable-next-line complexity
	renderPlayerScore(match_status: string, player: Object, double_score?: boolean) {
		const { round, has_assistant_coach } = this.props;
		const status = match_status || 'scheduled';
		const score = {
			playing: getPlayerScoreByRound(player, round.id, '0'),
			complete: getPlayerScoreByRound(player, round.id, 'DNP'),
			scheduled: has_assistant_coach?
				_.get(player.stats, `proj_scores[${round.id}]`, '--'):
				'--'
		};

		const display_score = (/[0-9]{1,}/.test(score[status]) && double_score === true)?
			parseInt(score[status], 10) * 2 : score[status];

		const renderScore = () => (
			<Score
				match_status={status}
				has_assistant_coach={has_assistant_coach}
				value={score[status]}
			>
				{display_score}
			</Score>
		);

		if (has_assistant_coach && match_status === "scheduled") {
			return (
				<ProjectedScoreContainer>
					{renderScore()}
				</ProjectedScoreContainer>
			);
		}

		return renderScore();
	}

	// eslint-disable-next-line complexity
	renderPlayer(team: Object, player: Object, position: Object, align: 'left' | 'right' = 'left') {
		const { round, openPlayerPopup, positions } = this.props;

		if (!player || !round || !team) {
			return <PlayerRowPart align={align}></PlayerRowPart>;
		}


		const player_squad_match = getSquadMatch(_.get(round, 'matches'), player.squad_id);

		const match_status = _.get(player_squad_match, 'status');
		const special_position = this.getPlayerSpecialPosition(team, player.id);

		const is_captain = team.lineup.captain === player.id;
		const is_vice_captain = team.lineup.vice_captain === player.id;
		const is_emergency_enabled = team.lineup.emergency &&
			Boolean(Object.values(team.lineup.emergency)
				.find(value => value === player.id));
		const is_emergency = !is_emergency_enabled && position.id === 'bench';
		const reverse = align === 'right';

		let label = position.short_name || 'INT';
		if(is_emergency_enabled) {
			// get position id
			const emerg_pos_id = Object.keys(team.lineup.emergency)
				.find(id => team.lineup.emergency[id] === player.id);
			// Get correct pos object
			const position_object = positions.find(pos => pos.id.toString() === emerg_pos_id);
			// $FlowFixMe
			label = position_object.short_name;
		}

		// check if we should double players score
		const double_score = this.shouldPlayersScoreBeDoubled(player, team, round);

		return (
			<PlayerRowPart
				reverse={reverse}
				players_score_does_not_count={
					this.playersScoreDoesNotCount(
						player,team,
						is_emergency_enabled,
						is_emergency,
						position)}
			>
				<StyledRotatedLabel
					text={label}
					is_captain={is_captain}
					is_vice_captain={is_vice_captain}
					is_emergency={is_emergency}
					is_emergency_enabled={is_emergency_enabled}
				/>
				<Player align={align} special_position={special_position} reverse={reverse}>
					<PlayerInfoStyled
						player={player}
						reverse={reverse}
						hide_favourite
						is_captain={is_captain}
						is_vice_captain={is_vice_captain}
						is_emergency={is_emergency}
						short_names={true}
						openPlayerPopup={openPlayerPopup}
						short_opponent={true}
					/>
					<ScoreWrapper>
						{this.renderPlayerScore(match_status, player, double_score)}
					</ScoreWrapper>
				</Player>
			</PlayerRowPart>
		);
	}

	renderPlayerRow(home_team_player_id: number, away_team_player_id: number, position: Object) {
		const {
			players,
			home_team,
			away_team
		} = this.props;

		const home_team_player = _.get(players, home_team_player_id);
		const away_team_player = _.get(players, away_team_player_id);
		return (
			<PlayerComparisonRow key={`${home_team_player_id}-${away_team_player_id}`}>
				{this.renderPlayer(home_team, home_team_player, position)}
				{this.renderPlayer(away_team, away_team_player, position, 'right')}
			</PlayerComparisonRow>
		);
	}

	renderPositionPlayerSection(position: Object) {
		const {
			home_team = {},
			away_team  = {},
		} = this.props;

		const home_team_lineup_position_player_ids = _.get(home_team, ["lineup", position.id]);
		const away_team_lineup_position_player_ids = _.get(away_team, ["lineup", position.id]);
		const home_team_lineup_position_player_count = _.size(home_team_lineup_position_player_ids);
		const away_team_lineup_position_player_count = _.size(away_team_lineup_position_player_ids);
		const num_rows = _.max([
			home_team_lineup_position_player_count,
			away_team_lineup_position_player_count
		]);

		return (
			<React.Fragment key={position.id}>
				{this.renderPositionHeader(
					position,
					home_team_lineup_position_player_ids,
					away_team_lineup_position_player_ids
				)}
				{_.times(num_rows, index =>
					this.renderPlayerRow(
						_.get(home_team_lineup_position_player_ids, index),
						_.get(away_team_lineup_position_player_ids, index),
						position
					)
				)}
			</React.Fragment>
		);
	}

	render() {
		return (
			<Container>
				<PositionsPlayerList>
					{this.positions_list && this.positions_list.map(position =>
						this.renderPositionPlayerSection(position)
					)}
				</PositionsPlayerList>
			</Container>
		);
	}
}

export default TeamsComparisonTable;


// WEBPACK FOOTER //
// ./src/pages/league/matchup/teamsComparisonTable.js